import { REACT_APP_BACKEND_PROTECTED_URL } from "../../configs/config";
import { IListMyOrganizationsResponse, IMyOrganizationEntry } from "../../types/organization/IMyOrganizationEntry";
import { IOrganization } from "../../types/organization/IOrganization";
import { IOrganizationInvitation } from "../../types/organization/IOrganizationInvitation";
import { IOrganizationMember } from "../../types/organization/IOrganizationMember";
import {
  IChangeMemberRoleRequest,
  ICreateOrganizationRequest,
  IInviteMemberRequest,
  IListInvitationsResponse,
  IListMembersResponse,
  IOrganizationCapacity,
  IOrganizationCapacityCheckoutRequest,
  IOrganizationCapacityQuote,
  IOrganizationCheckoutSessionResponse,
  IOrganizationTokenPurchaseRequest,
  IOrganizationTokenSettings,
  IUpdateOrganizationRequest,
  IUpdateOrganizationTokenSettingsRequest,
  IWalletBalanceResponse,
  IWalletTransaction,
  IWalletTransactionsResponse,
} from "../../types/organization/OrganizationServiceTypes";

// Re-export request/response types so existing call sites that imported
// them from this module keep working without code changes.
export type {
  IChangeMemberRoleRequest,
  ICreateOrganizationRequest,
  IInviteMemberRequest,
  IListInvitationsResponse,
  IListMembersResponse,
  IMyOrganizationEntry,
  IOrganizationCapacity,
  IOrganizationCapacityCheckoutRequest,
  IOrganizationCapacityQuote,
  IOrganizationCheckoutSessionResponse,
  IOrganizationTokenPurchaseRequest,
  IOrganizationTokenSettings,
  IUpdateOrganizationRequest,
  IUpdateOrganizationTokenSettingsRequest,
  IWalletBalanceResponse,
  IWalletTransaction,
  IWalletTransactionsResponse,
};

const ORGANIZATIONS_BASE = `${REACT_APP_BACKEND_PROTECTED_URL}/organizations`;

/**
 * Service for the enterprise organization REST endpoints. Mirrors the
 * routes mounted under /v1/protected/organizations in
 * muggle-ai-prompt-service. Each method is a thin wrapper around fetch
 * that returns parsed JSON or throws on non-2xx.
 */
export class OrganizationService {
  // -------------------------------------------------------------------
  // Organization CRUD
  // -------------------------------------------------------------------

  /**
   * Create a new organization. The calling user becomes the OWNER.
   *
   * @param request - Body containing the organization display name.
   * @param authToken - Bearer token from Auth0.
   * @returns The newly created organization.
   */
  static async createOrganization(request: ICreateOrganizationRequest, authToken: string): Promise<IOrganization> {
    const response = await fetch(ORGANIZATIONS_BASE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to create organization: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Fetch a single organization by id. Requires MEMBER role on the org.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   * @returns The organization, or throws on 404 / auth errors.
   */
  static async getOrganization(orgId: string, authToken: string): Promise<IOrganization> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch organization: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update mutable fields on an organization. Requires OWNER role.
   *
   * @param orgId - The organization id.
   * @param updates - Partial set of fields to update.
   * @param authToken - Bearer token from Auth0.
   * @returns The updated organization.
   */
  static async updateOrganization(
    orgId: string,
    updates: IUpdateOrganizationRequest,
    authToken: string
  ): Promise<IOrganization> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to update organization: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Cancel an organization. Marks the org as CANCELED on the backend
   * and deactivates all members. Requires OWNER role.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   */
  static async deleteOrganization(orgId: string, authToken: string): Promise<void> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete organization: ${response.statusText}`);
    }
  }

  /**
   * List every organization the authenticated user currently belongs
   * to, paired with the caller's role in each. Backed by
   * `GET /v1/protected/organizations/my-organizations`.
   *
   * The endpoint is intentionally caller-scoped (no `requireOrgRole`
   * gate) so a user with zero memberships still gets a valid empty
   * response and the suspended-state UI can render CANCELED /
   * SUSPENDED orgs the caller belongs to.
   *
   * @param authToken - Bearer token from Auth0.
   * @returns Array of `{ organization, role }` entries, possibly empty.
   */
  static async listMyOrganizations(authToken: string): Promise<IMyOrganizationEntry[]> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/my-organizations`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to list my organizations: ${errorBody}`);
    }

    const body: IListMyOrganizationsResponse = await response.json();
    return body.organizations;
  }

  // -------------------------------------------------------------------
  // Members
  // -------------------------------------------------------------------

  /**
   * List all members of an organization. Requires MEMBER role.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   * @returns Array of organization members.
   */
  static async listMembers(orgId: string, authToken: string): Promise<IOrganizationMember[]> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/members`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to list members: ${response.statusText}`);
    }

    const body: IListMembersResponse = await response.json();
    return body.members;
  }

  /**
   * Invite a new member to an organization. Requires ADMIN role.
   *
   * @param orgId - The organization id.
   * @param request - Email + role for the invitation.
   * @param authToken - Bearer token from Auth0.
   * @returns The newly created invitation record.
   */
  static async inviteMember(
    orgId: string,
    request: IInviteMemberRequest,
    authToken: string
  ): Promise<IOrganizationInvitation> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/members/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to invite member: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Remove a member from an organization. Requires ADMIN role.
   *
   * @param orgId - The organization id.
   * @param memberId - The membership record id.
   * @param authToken - Bearer token from Auth0.
   */
  static async removeMember(orgId: string, memberId: string, authToken: string): Promise<void> {
    const response = await fetch(
      `${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/members/${encodeURIComponent(memberId)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to remove member: ${response.statusText}`);
    }
  }

  /**
   * Change the role of an existing member. Requires OWNER role on the
   * org. The OWNER role itself cannot be assigned via this endpoint.
   *
   * @param orgId - The organization id.
   * @param memberId - The membership record id.
   * @param request - New role for the member.
   * @param authToken - Bearer token from Auth0.
   * @returns The updated member record.
   */
  static async changeMemberRole(
    orgId: string,
    memberId: string,
    request: IChangeMemberRoleRequest,
    authToken: string
  ): Promise<IOrganizationMember> {
    const response = await fetch(
      `${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/members/${encodeURIComponent(memberId)}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to change member role: ${errorBody}`);
    }

    return response.json();
  }

  // -------------------------------------------------------------------
  // Invitations
  // -------------------------------------------------------------------

  /**
   * List pending invitations for an organization. Requires ADMIN role.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   * @returns Array of invitations still in PENDING status.
   */
  static async listPendingInvitations(orgId: string, authToken: string): Promise<IOrganizationInvitation[]> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/invitations`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to list invitations: ${response.statusText}`);
    }

    const body: IListInvitationsResponse = await response.json();
    return body.invitations;
  }

  /**
   * Revoke (cancel) a pending invitation. Requires ADMIN role.
   *
   * @param orgId - The organization id.
   * @param invitationId - The invitation document id.
   * @param authToken - Bearer token from Auth0.
   */
  static async revokeInvitation(orgId: string, invitationId: string, authToken: string): Promise<void> {
    const response = await fetch(
      `${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to revoke invitation: ${response.statusText}`);
    }
  }

  /**
   * Accept an invitation token. The signed-in user becomes a member of
   * the org referenced by the token. The org id is intentionally not in
   * the URL because the invitee may have no membership yet.
   *
   * @param token - The opaque invitation token from the acceptance URL.
   * @param authToken - Bearer token from Auth0.
   * @returns The newly created member record.
   */
  static async acceptInvitation(token: string, authToken: string): Promise<IOrganizationMember> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/invitations/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token: token }),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to accept invitation: ${errorBody}`);
    }

    return response.json();
  }

  // -------------------------------------------------------------------
  // Wallet
  // -------------------------------------------------------------------

  /**
   * Fetch the token balance for an organization wallet. Requires
   * MEMBER role.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   * @returns The current token balance for the org wallet.
   */
  static async getWalletBalance(orgId: string, authToken: string): Promise<number> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/wallet/balance`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch wallet balance: ${response.statusText}`);
    }

    const body: IWalletBalanceResponse = await response.json();
    return body.tokenBalance;
  }

  /**
   * Fetch the most recent transactions for an organization wallet.
   * Requires MEMBER role.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   * @param limit - Optional cap on the number of transactions returned.
   * @returns Array of wallet transactions, newest first.
   */
  static async getWalletTransactions(orgId: string, authToken: string, limit?: number): Promise<IWalletTransaction[]> {
    const query = typeof limit === "number" ? `?limit=${encodeURIComponent(String(limit))}` : "";
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/wallet/transactions${query}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch wallet transactions: ${response.statusText}`);
    }

    const body: IWalletTransactionsResponse = await response.json();
    return body.transactions;
  }

  /**
   * Start a Stripe Checkout for buying organization tokens. Requires
   * ADMIN role; the card used is saved for auto top-up.
   *
   * @param orgId - The organization id.
   * @param request - Whole-dollar amount and the Checkout return URLs.
   * @param authToken - Bearer token from Auth0.
   * @returns The Checkout session, whose URL the caller redirects to.
   */
  static async createTokenPurchaseCheckout(
    orgId: string,
    request: IOrganizationTokenPurchaseRequest,
    authToken: string
  ): Promise<IOrganizationCheckoutSessionResponse> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/wallet/topup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to start token purchase: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Fetch the organization's low-balance and auto top-up settings.
   * Requires MEMBER role.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   * @returns The settings with the level resolved and the purchase terms.
   */
  static async getTokenSettings(orgId: string, authToken: string): Promise<IOrganizationTokenSettings> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/wallet/settings`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to fetch token settings: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Replace the organization's low-balance and auto top-up settings.
   * Requires OWNER role.
   *
   * @param orgId - The organization id.
   * @param request - The full settings to save.
   * @param authToken - Bearer token from Auth0.
   * @returns The saved settings.
   */
  static async updateTokenSettings(
    orgId: string,
    request: IUpdateOrganizationTokenSettingsRequest,
    authToken: string
  ): Promise<IOrganizationTokenSettings> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/wallet/settings`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to save token settings: ${errorBody}`);
    }

    return response.json();
  }

  // -------------------------------------------------------------------
  // Capacity
  // -------------------------------------------------------------------

  /**
   * Fetch the organization's concurrency capacity. Requires MEMBER role.
   *
   * @param orgId - The organization id.
   * @param authToken - Bearer token from Auth0.
   * @returns The ceiling, the level in force and any scheduled reduction.
   */
  static async getCapacity(orgId: string, authToken: string): Promise<IOrganizationCapacity> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/capacity`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to fetch capacity: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Fetch the monthly price of a concurrency level. Requires MEMBER role.
   *
   * @param orgId - The organization id.
   * @param level - Concurrent workflows to price.
   * @param authToken - Bearer token from Auth0.
   * @returns The level's monthly amount in cents.
   */
  static async getCapacityQuote(orgId: string, level: number, authToken: string): Promise<IOrganizationCapacityQuote> {
    const response = await fetch(
      `${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/capacity/quote?level=${encodeURIComponent(String(level))}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to price capacity: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Start the Enterprise subscription on a chosen level. Requires OWNER role.
   *
   * @param orgId - The organization id.
   * @param request - The level and the Checkout return URLs.
   * @param authToken - Bearer token from Auth0.
   * @returns The Checkout session, whose URL the caller redirects to.
   */
  static async createCapacityCheckout(
    orgId: string,
    request: IOrganizationCapacityCheckoutRequest,
    authToken: string
  ): Promise<IOrganizationCheckoutSessionResponse> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/subscription/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to start the subscription: ${errorBody}`);
    }

    return response.json();
  }

  /**
   * Change the concurrency level: a raise applies now, a reduction from the
   * next renewal. Requires OWNER role.
   *
   * @param orgId - The organization id.
   * @param level - The new level.
   * @param authToken - Bearer token from Auth0.
   * @returns The capacity after the change.
   */
  static async changeCapacityLevel(orgId: string, level: number, authToken: string): Promise<IOrganizationCapacity> {
    const response = await fetch(`${ORGANIZATIONS_BASE}/${encodeURIComponent(orgId)}/capacity/level`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ level: level }),
    });

    if (!response.ok) {
      const errorBody = await safeReadErrorBody(response);
      throw new Error(`Failed to change capacity: ${errorBody}`);
    }

    return response.json();
  }
}

/**
 * Reads a non-2xx response body and extracts a useful message string.
 * Falls back to the HTTP status text when the body cannot be parsed.
 */
async function safeReadErrorBody(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (data && typeof data === "object" && typeof data.message === "string") {
      return data.message;
    }
    if (data && typeof data === "object" && typeof data.error === "string") {
      return data.error;
    }
    return response.statusText;
  } catch {
    return response.statusText;
  }
}
